import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { NotificationContext } from './context';
import { useSignalRNotifications } from 'hooks/useSignalRNotifications';
import notificationSound from 'assets/audio/notification.wav';

/**
 * Notification Provider
 * Manages SignalR real-time notifications for the entire application
 */
export function NotificationProvider({ children }) {
  // Get access token from localStorage
  const accessToken = typeof window !== 'undefined' 
    ? localStorage.getItem('authToken') 
    : null;

  // Create audio reference for notification sound
  const audioRef = useRef(null);

  // Initialize audio on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const audio = new Audio(notificationSound);
    audio.preload = 'auto';
    audio.volume = 0.5; // Set volume to 50%
    audio.load();
    audioRef.current = audio;

    const unlockAudio = () => {
      if (!audioRef.current) {
        return;
      }

      audioRef.current
        .play()
        .then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        })
        .catch(() => {
          // Ignore unlock errors (browser may block until user interacts again)
        });

      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };

    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    if (audioRef.current) {
      // Reset audio to start if it's already playing
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(error => {
        console.warn('Failed to play notification sound:', error);
      });
    }
  };

  // Function to strip HTML tags but preserve link text for toast notifications
  const stripHtmlForToast = (html) => {
    if (!html) return '';
    // Replace anchor tags with just the link text
    let cleaned = html.replace(/<a[^>]*>(.*?)<\/a>/gi, '$1');
    // Remove any remaining HTML tags
    cleaned = cleaned.replace(/<[^>]*>/g, '');
    return cleaned;
  };

  // Function to extract URL from anchor tag
  const extractUrlFromHtml = (html) => {
    if (!html) return null;
    const match = html.match(/<a[^>]*href=['"]([^'"]*)['"]/i);
    return match ? match[1] : null;
  };

  // Initialize SignalR notifications
  const notificationState = useSignalRNotifications({
    accessToken,
    autoConnect: !!accessToken, // Only auto-connect if token exists
    onNotification: (notification) => {
      console.info('🔔 New notification received:', notification);
      
      // Play notification sound
      playNotificationSound();
      
      // Show toast notification with clean text
      // const rawMessage = notification.message || notification.Message || 'New notification received';
      // const cleanMessage = stripHtmlForToast(rawMessage);
      // const hasLinks = /<a[^>]*>/i.test(rawMessage);
      // const extractedUrl = hasLinks ? extractUrlFromHtml(rawMessage) : null;
      
      toast.info(cleanMessage, {
        position: 'top-right',
        duration: 8000, // Increased duration for link interaction
        description: hasLinks && extractedUrl ? (
          <span>
            <a 
              href={extractedUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation(); // Prevent toast from closing
              }}
            >
              Click here to see
            </a>
          </span>
        ) : stripHtmlForToast(notification.description || notification.Description || ''),
      });
    },
    onError: (error) => {
      console.error('❌ Notification error:', error);
      
      // Only show error toast in development or for critical errors
      if (import.meta.env.DEV) {
        toast.error('Notification service error', {
          description: error.message || 'Failed to connect to notification service',
        });
      }
    }
  });

  // Log connection state changes
  useEffect(() => {
    console.info('📡 Notification Connection State:', notificationState.connectionState);
  }, [notificationState.connectionState]);

  return (
    <NotificationContext.Provider value={notificationState}>
      {children}
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

